import { useMemo } from 'react';
import { Checkbox } from './index';
import { FILTERS_WITH_SELECTION, FILTER_TYPES } from '@config/filters';
import { formatPriceValue, formatMileageValue } from '@utils';

const Selection = ({ facets = {} }) => {
    /**
     * Get only the filters that are supported.
     */
    const supportedFilters = useMemo(
        () =>
            Object.entries(facets).filter(([name]) =>
                FILTERS_WITH_SELECTION.includes(name)
            ),
        [facets]
    );

    /**
     * Get all the items that have an option selected as true and put it into a new array.
     */
    const selectedFilters = useMemo(
        () =>
            supportedFilters.filter(([, { options }]) =>
                options.some(({ selected }) => selected === true)
            ),
        [supportedFilters]
    );

    /**
     * Format the name of the range.
     * In this case we need to combine the two values to a single string.
     *
     * @param {string} title
     * @param {string} from
     * @param {string} to
     * @returns {string}
     */
    const formatRangeName = (title, from, to) => {
        switch (title) {
            case 'PriceOnline':
            case 'PriceLease':
                return `${formatPriceValue(from.name)} - ${formatPriceValue(
                    to.name
                )}`;
            case 'Mileage':
                return `${formatMileageValue(from.name)} - ${formatMileageValue(
                    to.name
                )}`;
            default:
                return `${from?.name} - ${to?.name}`;
        }
    };

    /**
     * Return null if there are no selected items
     */
    if (facets === undefined || !selectedFilters.length) {
        return null;
    }

    return (
        <ul className="op-selection">
            {selectedFilters.map(([title, { options }], index) => {
                const filterType = FILTER_TYPES[title];

                if (filterType === 'checkbox') {
                    return options.map(({ selected, name, count, value }) => (
                        <Checkbox
                            key={`${title}-${name}`}
                            title={title}
                            isSelected={selected}
                            name={name}
                            count={count}
                            value={value}
                            type={`selection`}
                        />
                    ));
                }

                if (filterType === 'range') {
                    const [from, to] = options
                        .filter(({ selected }) => selected === true)
                        /**
                         * Sometimes there is a string value, like 'overig', which we have to account for.
                         * So we check if the value is a number or not and order that too.
                         * @since 1.0.12
                         */
                        .sort((a, b) => {
                            if (isNaN(a.value)) {
                                return 1; // Go down.
                            }

                            if (isNaN(b.value)) {
                                return -1; // Go up.
                            }

                            return a.value - b.value;
                        }); // Always go from low to high order.

                    if (
                        typeof from === 'undefined' ||
                        typeof to === 'undefined'
                    ) {
                        return null;
                    }

                    const formattedName = formatRangeName(title, from, to);
                    return (
                        <Checkbox
                            key={`${title}-${from.name}-${to.name}`}
                            title={title}
                            isSelected={true}
                            name={formattedName}
                            count={0}
                            value={`${from.value} - ${to.value}`}
                            type={`selection`}
                        />
                    );
                }

                return null;
            })}
        </ul>
    );
};

export default Selection;
